import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import './Sidebar.css';
import { FaChevronRight, FaChevronLeft, FaUndo, FaTrash } from 'react-icons/fa';

const Sidebar = () => {
  const { currentTool, setTool, undo, deleteShape, selectedShapeId } = useApp();
  const [activeCategory, setActiveCategory] = useState('shapes');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showTooltip, setShowTooltip] = useState(null);

  // Keyboard shortcut to toggle sidebar (Ctrl/Cmd + B)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'b') {
        event.preventDefault();
        setIsCollapsed(prev => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const categories = {
    shapes: {
      icon: '📐',
      label: 'Shapes',
      tools: [
        { 
          id: 'rectangle', 
          label: 'Room', 
          icon: '⬜', 
          description: 'Add rectangular rooms',
          color: '#1a1a1a',
          gradient: 'linear-gradient(135deg, #1a1a1a, #404040)',
          shortcut: 'R'
        },
        { 
          id: 'line', 
          label: 'Wall/Line', 
          icon: '📏', 
          description: 'Draw walls and lines',
          color: '#0d0d0d',
          gradient: 'linear-gradient(135deg, #0d0d0d, #2a2a2a)',
          shortcut: 'L'
        }
      ]
    },
    structural: {
      icon: '🏠',
      label: 'Structural',
      tools: [
        { 
          id: 'door', 
          label: 'Door', 
          icon: '🚪', 
          description: 'Add doors to rooms',
          color: '#606060',
          gradient: 'linear-gradient(135deg, #606060, #808080)',
          shortcut: 'D'
        },
        { 
          id: 'stairs', 
          label: 'Stairs', 
          icon: '🪜', 
          description: 'Add staircases',
          color: '#505050',
          gradient: 'linear-gradient(135deg, #505050, #707070)',
          shortcut: 'S'
        }
      ]
    },
    furniture: {
      icon: '🪑',
      label: 'Furniture',
      tools: [
        { 
          id: 'bed', 
          label: 'Bed', 
          icon: '🛏️', 
          description: 'Add beds',
          color: '#303030',
          gradient: 'linear-gradient(135deg, #303030, #505050)',
          shortcut: 'B'
        },
        { 
          id: 'chair', 
          label: 'Chair', 
          icon: '🪑', 
          description: 'Add chairs',
          color: '#202020',
          gradient: 'linear-gradient(135deg, #202020, #404040)',
          shortcut: 'H'
        },
        { 
          id: 'couch', 
          label: 'Sofa', 
          icon: '🛋️', 
          description: 'Add sofas and couches',
          color: '#101010',
          gradient: 'linear-gradient(135deg, #101010, #303030)',
          shortcut: 'O'
        },
        { 
          id: 'table', 
          label: 'Table', 
          icon: '🍽️', 
          description: 'Add dining tables',
          color: '#252525',
          gradient: 'linear-gradient(135deg, #252525, #454545)',
          shortcut: 'E'
        },
        { 
          id: 'tv', 
          label: 'TV', 
          icon: '📺', 
          description: 'Add televisions',
          color: '#151515',
          gradient: 'linear-gradient(135deg, #151515, #353535)',
          shortcut: 'V'
        }
      ]
    },
    fixtures: {
      icon: '🚿',
      label: 'Fixtures',
      tools: [
        { 
          id: 'toilet', 
          label: 'Toilet', 
          icon: '🚽', 
          description: 'Add toilets',
          color: '#353535',
          gradient: 'linear-gradient(135deg, #353535, #555555)',
          shortcut: 'I'
        },
        { 
          id: 'bathtub', 
          label: 'Bathtub', 
          icon: '🛁', 
          description: 'Add bathtubs',
          color: '#454545',
          gradient: 'linear-gradient(135deg, #454545, #656565)',
          shortcut: 'U'
        }
      ]
    },
    outdoor: {
      icon: '🌳',
      label: 'Outdoor',
      tools: [
        { 
          id: 'tree', 
          label: 'Tree/Plant', 
          icon: '🌳', 
          description: 'Add trees and plants',
          color: '#555555',
          gradient: 'linear-gradient(135deg, #555555, #757575)',
          shortcut: 'P'
        },
        { 
          id: 'car', 
          label: 'Vehicle', 
          icon: '🚗', 
          description: 'Add cars and vehicles',
          color: '#656565',
          gradient: 'linear-gradient(135deg, #656565, #858585)',
          shortcut: 'A'
        }
      ]
    }
  };

  const handleToolSelect = (toolId) => {
    setTool(toolId);
  };

  const handleCategorySelect = (categoryId) => {
    setActiveCategory(categoryId);
  };

  const handleTooltipShow = (toolId) => {
    if (isCollapsed) {
      setShowTooltip(toolId);
    }
  };

  const handleTooltipHide = () => {
    setShowTooltip(null);
  };

  return (
    <div className={`modern-sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      {/* Header */}
      <div className="sidebar-header">
        <div className="sidebar-title">
          <i className="title-icon fas fa-layer-group"></i>
          {!isCollapsed && <span>Floor Planz</span>}
        </div>
        {/* Undo and Delete Buttons */}
       
        <button
          className="collapse-btn"
          onClick={() => setIsCollapsed(!isCollapsed)}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={`${isCollapsed ? 'Expand' : 'Collapse'} sidebar (Ctrl+B)`}
        >
        {isCollapsed?<FaChevronRight />:<FaChevronLeft/>}
        </button>
      </div>
      <div className="sidebar-actions" style={{ width:"100%",justifyContent:"center",display: 'flex',flexWrap:isCollapsed?"wrap":"nowrap", gap: '8px', margin: '12px 0' }}>
          <button
            className="sidebar-action-btn"
            title="Undo"
            onClick={undo}
            style={{ fontSize: '15px', padding: '6px 10px', borderRadius: '6px', border: 'none', background: '#eee', cursor: 'pointer' }}
          >
            <FaUndo /> {!isCollapsed && <span style={{ marginLeft: 6 }}>Undo</span>}
          </button>
          <button
            className="sidebar-action-btn"
            title="Delete Selected"
            onClick={() => selectedShapeId && deleteShape(selectedShapeId)}
            disabled={!selectedShapeId}
            style={{ fontSize: '15px', padding: '6px 10px', borderRadius: '6px', border: 'none', background: selectedShapeId ? '#f44336' : '#eee', color: selectedShapeId ? 'white' : '#888', cursor: selectedShapeId ? 'pointer' : 'not-allowed' }}
          >
            <FaTrash /> {!isCollapsed && <span style={{ marginLeft: 6 }}>Delete</span>}
          </button>
        </div>

      {/* Category Tabs */}
      <div className="category-tabs">
        {Object.entries(categories).map(([categoryId, category]) => (
          <button
            key={categoryId}
            className={`category-tab ${activeCategory === categoryId ? 'active' : ''}`}
            onClick={() => handleCategorySelect(categoryId)}
            title={isCollapsed ? category.label : ''}
          >
            <span>{category.icon}</span>
            {!isCollapsed && <span>{category.label}</span>}
          </button>
        ))}
      </div>

      {/* Tools Container */}
      <div className="tools-container">
        {categories[activeCategory]?.tools.map((tool, index) => (
          <div key={tool.id} className="tool-wrapper">
            <button
              className={`modern-tool-button ${currentTool === tool.id ? 'active' : ''}`}
              onClick={() => handleToolSelect(tool.id)}
              onMouseEnter={() => handleTooltipShow(tool.id)}
              onMouseLeave={handleTooltipHide}
              style={{
                '--tool-color': tool.color,
                '--tool-gradient': tool.gradient,
                '--animation-delay': `${index * 0.1}s`
              }}
            >
              <div className="tool-icon">
                {tool.icon}
              </div>
              {!isCollapsed && (
                <div className="tool-info">
                  <div className="tool-label">{tool.label}</div>
                  <div className="tool-description">{tool.description}</div>
                </div>
              )}
              <div className="tool-glow"></div>
            </button>
            
            {/* Tooltip for collapsed state */}
            {isCollapsed && showTooltip === tool.id && (
              <div className="tool-tooltip">
                <div className="tooltip-content">
                  <div className="tooltip-label">{tool.label}</div>
                  <div className="tooltip-description">{tool.description}</div>
                  <div className="tooltip-shortcut">Press <kbd>{tool.shortcut}</kbd></div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Keyboard Shortcuts */}
      {!isCollapsed && (
        <div className="keyboard-shortcuts">
          <h4>Keyboard Shortcuts</h4>
          <div className="shortcut-list">
            <div className="shortcut-item">
              <span>Toggle Sidebar</span>
              <kbd>Ctrl+B</kbd>
            </div>
            {categories[activeCategory]?.tools.slice(0, 4).map((tool) => (
              <div key={tool.id} className="shortcut-item">
                <span>{tool.label}</span>
                <kbd>{tool.shortcut}</kbd>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar; 